import { match } from "ts-pattern";
/**
 * The base of any error returned by a Result or ResultAsync
 */
export class BaseError extends Error {
    /**
     * The discriminate used to quickly identify the error.
     */
    name;
    /**
     * The suggested HTTP status code for this error.
     */
    status;
    /**
     * A human-readable message describing the error.
     */
    message;
    /**
     * An array of issues underlying the error. Useful for detailed debugging.
     */
    issues;
    /**
     * Errors which were suppressed by this error. Useful for error chaining.
     * Inspired by https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SuppressedError
     */
    suppressed;
    constructor({ name, status, message, issues, suppressed, }) {
        super(message);
        this.name = name;
        this.status = status;
        this.message = message;
        this.issues = issues;
        this.suppressed = suppressed;
        // Stryker disable next-line CallExpression: native class emit already derives the prototype from new.target, so removing this call is an equivalent mutant; it is kept as insurance against a down-levelled emit target
        Object.setPrototypeOf(this, new.target.prototype);
    }
    /**
     * Prettify the error for logging or displaying.
     * Inspired by Zod https://zod.dev/error-formatting?id=zprettifyerror
     * @returns
     */
    prettifyError({ includeStack } = {}) {
        const message = `✖ ${this.name} [${this.status.toFixed()}]: ${this.message}`;
        const issues = this.issues?.map((issue) => `  → ${issue}`) ?? [];
        const flattenedSuppressedErrors = this._flattenSuppressedErrors(this);
        const suppressedErrorCount = flattenedSuppressedErrors.length;
        const suppressed = flattenedSuppressedErrors.map((err, index) => this._prettifySuppressedError(err, index + 1, suppressedErrorCount));
        const stack = includeStack && this.stack ? ["Stack trace:", this.stack] : [];
        return [message, ...issues, ...suppressed, ...stack].join("\n");
    }
    _flattenSuppressedErrors(error) {
        const directErrors = error.suppressed ?? [];
        const indirectErrors = directErrors
            .flatMap((err) => [err, this._flattenSuppressedErrors(err)])
            .flat();
        return indirectErrors;
    }
    _prettifySuppressedError(error, index, total) {
        const message = `  ✖ Suppresses (${index.toFixed()}/${total.toFixed()}): ${error.name} [${error.status.toFixed()}]: ${error.message}`;
        const issues = error.issues?.map((issue) => `    → ${issue}`) ?? [];
        return [message, ...issues].join("\n");
    }
}
export const buildInvalidError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "invalid",
        status: 400,
        message: message ?? "Invalid",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 4 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        // Avoid buildInvalidError itself in the stack trace
        Error.captureStackTrace(error, buildInvalidError);
    }
    // Stryker restore all
    return error;
};
export const buildNotFoundError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "not-found",
        status: 404,
        message: message ?? "Not found",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 4 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        // Avoid buildNotFoundError itself in the stack trace
        Error.captureStackTrace(error, buildNotFoundError);
    }
    // Stryker restore all
    return error;
};
export const buildConflictError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "conflict",
        status: 409,
        message: message ?? "Conflict",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 4 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        // Avoid buildConflictError itself in the stack trace
        Error.captureStackTrace(error, buildConflictError);
    }
    // Stryker restore all
    return error;
};
export const buildUnauthorizedError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "unauthorized",
        status: 401,
        message: message ?? "Unauthorized",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 3 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        Error.captureStackTrace(error, buildUnauthorizedError);
    }
    // Stryker restore all
    return error;
};
export const buildForbiddenError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "forbidden",
        status: 403,
        message: message ?? "Forbidden",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 3 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        Error.captureStackTrace(error, buildForbiddenError);
    }
    // Stryker restore all
    return error;
};
export const buildInternalError = ({ message, issues, suppressed, } = {}) => {
    const error = new BaseError({
        name: "internal-error",
        status: 500,
        message: message ?? "Internal server error",
        issues,
        suppressed,
    });
    // Stryker disable all -- captureStackTrace always exists in V8/Node; both the branch and statement mutations are equivalent here
    /* v8 ignore next 4 -- false branch unreachable in V8/Node */
    if ("captureStackTrace" in Error) {
        // Avoid buildInternalError itself in the stack trace
        Error.captureStackTrace(error, buildInternalError);
    }
    // Stryker restore all
    return error;
};
export const toStandardError = (error) => match(error)
    // Deprecated string-based errors
    .with("conflict", () => buildConflictError())
    .with("db-error", () => buildInternalError())
    .with("not-found", () => buildNotFoundError())
    .with("validation-error", () => buildInvalidError())
    // Standard error types are passed through as a no-op.
    //
    // These six .with() patterns all share a single (err) => err handler.
    // ts-pattern's {} pattern matches any object, so replacing any individual
    // { name: "X" } with {} still routes every StandardError to the same
    // passthrough branch — the observable behaviour is identical for all six.
    // Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
    .with(
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "conflict" }, 
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "forbidden" }, 
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "internal-error" }, 
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "invalid" }, 
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "not-found" }, 
// Stryker disable next-line ObjectLiteral: {} still matches any StandardError object — equivalent mutant
{ name: "unauthorized" }, (err) => err)
    .exhaustive();
//# sourceMappingURL=error.js.map